// react/next
import React, { useRef } from 'react';
import { useState, useEffect } from 'react';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { useSelector } from 'react-redux';
import styles from './index.module.scss';
import fetchGameCover from '@/utils/fetch/fetch.js';
import { getDeviceType } from '@/utils/device';

const GameGrid = dynamic(() => import('components/GameGrid'), {
    ssr: true
});

// api
import {getAllGames, getCategoryGame, getGameCover} from '@/api/header.js';
import {formatCover} from "@/utils/methods";

export default function Index({ isSlugPage = false, initialDeviceType, initialGames = [], initialTotal = 0 }) {
    const [loading, setLoading] = useState(true);
    const [gameList, setGames] = useState(initialGames);
    const [hasMore, setHasMore] = useState(true);
    const [total, setTotal] = useState(initialTotal);
    const router = useRouter();
    const { slug } = router.query;
    const searchQuery = useSelector((state) => state.game.searchQuery);
    const refreshKey = useSelector((state) => state.game.refreshKey);
    const shouldFilter = useSelector((state) => state.game.shouldFilter);

    const [params, setParams] = useState({
        limit: initialDeviceType === 'mobile' ? 21 : 42,
        offset: 0,
        order: 'desc',
        sortby: 'create_at',
    });

    // 客户端水合后重新检测设备类型
    useEffect(() => {
        const deviceType = getDeviceType(navigator.userAgent, window.innerWidth);
        if (deviceType === 'mobile' && params.limit !== 21) {
            setParams(prev => ({ ...prev, limit: 21 }));
        } else if (deviceType === 'desktop' && params.limit !== 42) {
            setParams(prev => ({ ...prev, limit: 42 }));
        }
    }, []);

    // 加载更多游戏
    const loadMoreGames = async () => {
        if (!hasMore || loading) return;

        setLoading(true);
        try {
            let res;
            // 新的偏移量就是当前已加载的游戏数量
            const newOffset = gameList.length;

            if (slug && (slug !== 'all-game')) {
                res = await getCategoryGame({ 
                    category_path: slug, 
                    limit: params.limit, 
                    offset: newOffset // 从当前已加载的游戏数量开始
                });
            } else {
                res = await getAllGames({
                    ...params,
                    offset: newOffset
                });
            }

            let { results, total: totalCount } = res.data;
            results = results ? results.map((item) => ({
                ...item,
                loading: true,
            })) : [];

            // 只有在 shouldFilter 为 true 时才应用搜索过滤
            if (shouldFilter && searchQuery) {
                results = results.filter(item => 
                    item.name && item.name.toLowerCase().includes(searchQuery.toLowerCase())
                );
            }

            setTotal(totalCount);
            // 更新当前的 offset
            setParams(prev => ({
                ...prev,
                offset: newOffset
            }));
            
            // 添加新加载的游戏到列表中
            setGames(prev => [...prev, ...results]);
            
            // 更新游戏封面
            fetchGameCover(results, (newGames) => {
                setGames(prev => {
                    const prevGames = prev.slice(0, -results.length);
                    return [...prevGames, ...newGames];
                });
            });

            // 检查是否还有更多数据
            // 当前已加载的总数量（包括新加载的）小于总数，说明还有更多
            setHasMore(newOffset + results.length < totalCount);
        } catch (error) {
            console.error('Error fetching more games:', error);
        } finally {
            setLoading(false);
        }
    };

    // 初始化游戏列表
    useEffect(() => {
        // 如果路由未准备好，直接返回
        if (!router.isReady) return;

        // 重置状态
        setLoading(true);
        setGames([]);
        setParams(prev => ({ ...prev, offset: 0 })); // 重置偏移量为0
        setHasMore(true);

        // 按游戏类型获取
        const fetchGames = async () => {
            try {
                const res = await (((slug !== 'all-game') && slug) ?
                    getCategoryGame({ category_path: slug, limit: params.limit, offset: 0 }) : 
                    getAllGames({ ...params, offset: 0 }));
                
                let { results, total: totalCount } = res.data;
                results = results ? results.map((item) => ({
                    ...item,
                    loading: true,
                })) : [];

                // 只有在 shouldFilter 为 true 时才应用搜索过滤
                if (shouldFilter && searchQuery) {
                    results = results.filter(item => 
                        item.name && item.name.toLowerCase().includes(searchQuery.toLowerCase())
                    );
                }

                setTotal(totalCount);
                setGames(results);
                // 如果第一批数据量小于总数，说明还有更多数据
                setHasMore(results.length < totalCount);
                fetchGameCover(results, setGames);
            } catch (error) {
                console.error('Error fetching games:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchGames();
    }, [router.isReady, slug, searchQuery, shouldFilter]);

    // 生成页面标题
    const generatePageTitle = () => {
        if (!slug || slug === 'all-game') {
            return 'Play Online Games - Gameyeap';
        } else {
            // 处理带连字符的 slug，如 "puzzle-games" -> "Puzzle Games"
            const formattedSlug = slug
                .split('-')
                .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                .join(' ');
            return `${formattedSlug} Games - Gameyeap`;
        }
    };

    return (
        <>
            <h1 className={styles.page_title}>{generatePageTitle()}</h1>
            <GameGrid 
                gameList={gameList}
                loading={loading} 
                hasMore={hasMore}
                onLoadMore={loadMoreGames}
            />
        </>
    );
}

export const getServerSideProps = async context => {
    const userAgent = context.req.headers['user-agent'] || '';
    const deviceType = getDeviceType(userAgent);
    const limit = 200;
    const { slug } = context.query;

    try {
        const res = await (((slug !== 'all-game') && slug) ?
            getCategoryGame({ category_path: slug, limit, offset: 0 }) :
            getAllGames({ limit, offset: 0, order: 'desc', sortby: 'create_at' }));

        if (!res?.data?.results) {
            throw new Error('无效的API响应结构');
        }

        let { results, total } = res.data;
        const params = {
            game_ids: results.map((game) => game.id).join(','),
        }

        const coverRes = await getGameCover(params);

        const { results: gameCoversMap } = coverRes.data;
        results = results.map((game) => {
            let cover_image = gameCoversMap[game.id];
            if (cover_image) {
                cover_image = formatCover(cover_image);
            }
            return {
                ...game,
                loading: false,
                cover_image: cover_image || "/images/icons/common/placeholder.jpg",
            };
        });

        return {
            props: {
                page_name: '/',
                initialDeviceType: deviceType,
                initialGames: results,
                initialTotal: total
            }
        };
    } catch (error) {
        console.error('=== 服务端渲染错误 ===', {
            name: error.name,
            message: error.message,
            stack: error.stack
        });
        return {
            props: {
                page_name: '/',
                initialDeviceType: deviceType,
                initialGames: [],
                initialTotal: 0,
                error: process.env.NODE_ENV === 'development' ? {
                    message: error.message,
                    stack: error.stack
                } : undefined
            }
        };
    }
};
